import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import ZButton from "@/components/ZButton";
import { ZTextarea } from "@/components/ZInput";
import EmptyState from "@/components/EmptyState";
import { Users, Upload, ArrowLeft, CheckCircle2, Circle } from "lucide-react";

export default function VoteElectoralListPage() {
  const { id } = useParams();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailsRaw, setEmailsRaw] = useState("");
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/votes/${id}/electoral-list`)
      .then((r) => setList(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(load, [id]);

  const parseInput = () => {
    // Accept CSV (email,weight) or one email per line, or comma-separated emails
    const lines = emailsRaw.split(/[\n;]+/).map((l) => l.trim()).filter(Boolean);
    const emails = [];
    const weights = {};
    for (const line of lines) {
      const parts = line.split(/[,\t]/).map((s) => s.trim()).filter(Boolean);
      if (parts.length === 0) continue;
      const email = parts[0].toLowerCase();
      if (!email.includes("@")) continue;
      emails.push(email);
      if (parts[1]) {
        const w = parseFloat(parts[1]);
        if (!isNaN(w) && w > 0) weights[email] = w;
      }
    }
    return { emails, weights };
  };

  const upload = async () => {
    setErr(""); setSuccess("");
    const { emails, weights } = parseInput();
    if (emails.length === 0) return setErr("Aucun e-mail valide détecté.");
    setSubmitting(true);
    try {
      const r = await api.post(`/votes/${id}/electoral-list`, { emails, weights });
      setSuccess(`Liste enregistrée : ${r.data.count} votant(s).`);
      setEmailsRaw("");
      load();
      setTimeout(() => setSuccess(""), 4000);
    } catch (e) {
      setErr(errMsg(e));
    } finally { setSubmitting(false); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const text = await f.text();
    setEmailsRaw(text);
  };

  const votedCount = list.filter((x) => x.voted).length;

  return (
    <div className="z-container" style={{ maxWidth: 720 }} data-testid="electoral-list-page">
      <Link to="/org/events" className="z-link" style={{ display: "inline-flex", alignItems: "center", gap: 4, marginBottom: 14, fontSize: 14 }}>
        <ArrowLeft size={14} /> Retour
      </Link>
      <h1 className="z-page-title">Liste électorale</h1>
      <p className="z-page-sub">Définissez les votants autorisés (e-mail + pondération optionnelle).</p>

      <div className="z-card" style={{ padding: 22, marginBottom: 18 }}>
        <h3 style={{ marginTop: 0, fontSize: 15, fontWeight: 700, color: "var(--z-muted)" }}>Ajouter / Remplacer la liste</h3>
        <p className="z-help" style={{ marginTop: -4, marginBottom: 10 }}>
          Format : un e-mail par ligne, optionnellement suivi d'une virgule et d'un poids.
          <br />Exemple : <code>alice@x.com,3</code> = 3 voix pour Alice. <code>bob@x.com</code> = 1 voix.
        </p>
        <ZTextarea
          value={emailsRaw} onChange={(e) => setEmailsRaw(e.target.value)}
          placeholder={"alice@exemple.com,3\nbob@exemple.com\ncarol@exemple.com,2"}
          style={{ minHeight: 140, fontFamily: "monospace", fontSize: 13 }}
          data-testid="emails-textarea"
        />
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
          <label className="z-btn z-btn-outline z-btn-sm" style={{ cursor: "pointer" }}>
            <Upload size={14} /> Importer CSV/TXT
            <input type="file" accept=".csv,.txt" onChange={onFile} style={{ display: "none" }} data-testid="csv-import" />
          </label>
          <span className="z-help" style={{ margin: 0 }}>Remplace la liste actuelle</span>
        </div>
        {err && <p className="z-error-text">{err}</p>}
        {success && <p style={{ color: "var(--z-success)", fontWeight: 600, marginBottom: 8 }}>{success}</p>}
        <ZButton onClick={upload} loading={submitting} block data-testid="upload-list-btn">
          Enregistrer la liste électorale
        </ZButton>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Votants ({list.length})</h3>
        <span className="z-badge z-badge-teal">{votedCount} ont voté</span>
      </div>

      {loading ? (
        <div style={{ textAlign: "center" }}><span className="z-loader z-loader-dark" /></div>
      ) : list.length === 0 ? (
        <EmptyState icon={<Users size={28} />} title="Liste vide" message="Ajoutez des votants avec le formulaire ci-dessus." testId="list-empty" />
      ) : (
        <div style={{ display: "grid", gap: 6 }}>
          {list.map((x) => (
            <div key={x.email} className="z-card" style={{ padding: 10, display: "flex", alignItems: "center", gap: 10 }} data-testid={`voter-${x.email}`}>
              {x.voted ? <CheckCircle2 size={18} color="var(--z-success)" /> : <Circle size={18} color="var(--z-muted)" />}
              <span style={{ flex: 1, fontFamily: "monospace", fontSize: 13 }}>{x.email}</span>
              <span className="z-badge z-badge-teal">{x.weight} voix</span>
              {x.voted && <span className="z-badge z-badge-success">Voté</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
